import { useCallback, useMemo, useRef, useState, type DragEventHandler } from 'react';
import { cn, useToast } from '@immo/ui-kit';
import { useTranslations } from '../use-translations';
import {
  canMove,
  EMPTY_COLUMN_PREFERENCE,
  mergeSubsetOrder,
  moveColumn,
  resolveColumnOrder,
  setColumnVisible,
  type ColumnPreference,
} from './column-preference';
import type { ColumnPanelRow } from './columns-panel';
import type { ColumnsTriggerProps } from './columns-trigger';
import { useColumnDrag } from './use-column-drag';

export type ConfigurableColumn = { readonly id: string; readonly label: string };

const NO_UNAVAILABLE: ReadonlySet<string> = new Set();
const NO_LOCKED: ReadonlyArray<string> = [];
const NO_DEFAULT_HIDDEN: ReadonlyArray<string> = [];

export type ConfigurableColumnsOptions<C extends ConfigurableColumn> = {
  /** Every column the table can show, in today's order — which IS the default order. */
  readonly catalogue: ReadonlyArray<C>;
  readonly alwaysVisibleId: C['id'];
  /**
   * A leading block pinned by the table (sticky): always visible, always first in
   * catalogue order, never moved, and nothing can be moved or dropped before it.
   */
  readonly lockedLeadingIds?: ReadonlyArray<C['id']>;
  /** Columns that start hidden until the member's stored order has placed them; the panel can show them. */
  readonly defaultHiddenIds?: ReadonlyArray<C['id']>;
  /** Columns the table cannot show right now (a permission, a filter, the data). */
  readonly unavailableIds?: ReadonlySet<C['id']>;
  /** Controlled: the page holds `override ?? loaded` ABOVE its <Suspense> boundary. */
  readonly preference: ColumnPreference;
  readonly onPreferenceChange: (next: ColumnPreference) => void;
  /**
   * Persists the resolved preference. Resolves `false` — or rejects — when the write did not land;
   * the hook then shows the save-error toast. Must keep a stable identity.
   */
  readonly savePreference: (next: ColumnPreference) => Promise<boolean>;
  /** Passed through to <ColumnsTrigger> as aria-describedby. */
  readonly describedBy?: string;
};

export type ColumnHeadControls = {
  readonly columnLabel: string;
  readonly draggable: boolean; // more than one visible, unlocked column
  readonly dragged: boolean;
  readonly onDragStart: () => void;
  readonly onDragEnd: () => void;
  readonly canMoveLeft: boolean;
  readonly canMoveRight: boolean;
  readonly onMove: (direction: 'left' | 'right') => void;
  readonly onHide: (() => void) | null; // null for the always-visible column
  readonly onOpenPanel: () => void;
};

export type ConfigurableColumns<C extends ConfigurableColumn> = {
  readonly visibleColumns: ReadonlyArray<C>;
  /** Spread into <ColumnsTrigger>: open state, panel rows, visibility, reset, reorder. */
  readonly trigger: ColumnsTriggerProps;
  readonly headerRowProps: { readonly onDragLeave: DragEventHandler<HTMLTableRowElement> };
  readonly headCellProps: (id: C['id']) => {
    readonly onDragOver?: DragEventHandler<HTMLTableCellElement>;
    readonly onDrop?: DragEventHandler<HTMLTableCellElement>;
    readonly className: string; // 'group/col' + drop marker + select-none while dragging
  };
  readonly headControls: (id: C['id']) => ColumnHeadControls;
  /** Drop marker, dimming of the dragged column, select-none — merge into every body cell. */
  readonly bodyCellClassName: (id: C['id']) => string;
};

export function useConfigurableColumns<C extends ConfigurableColumn>({
  catalogue,
  alwaysVisibleId,
  lockedLeadingIds,
  defaultHiddenIds,
  unavailableIds,
  preference,
  onPreferenceChange,
  savePreference,
  describedBy,
}: ConfigurableColumnsOptions<C>): ConfigurableColumns<C> {
  const t = useTranslations('components.column-preference');
  const toast = useToast();
  const [panelOpen, setPanelOpen] = useState(false);
  const unavailable: ReadonlySet<string> = unavailableIds ?? NO_UNAVAILABLE;
  const lockedIds: ReadonlyArray<string> = lockedLeadingIds ?? NO_LOCKED;
  const defaultHidden: ReadonlyArray<string> = defaultHiddenIds ?? NO_DEFAULT_HIDDEN;
  const locked = useMemo(() => new Set(lockedIds), [lockedIds]);

  const catalogueIds = useMemo(() => catalogue.map((column) => column.id), [catalogue]);
  const resolved = useMemo(
    () => resolveColumnOrder(catalogueIds, alwaysVisibleId, preference, lockedIds, defaultHidden),
    [catalogueIds, alwaysVisibleId, preference, lockedIds, defaultHidden],
  );
  // Every move is computed over the unlocked tail, so nothing can land before the locked block.
  const lead = useMemo(() => resolved.order.filter((id) => locked.has(id)), [resolved.order, locked]);
  const tail = useMemo(() => resolved.order.filter((id) => !locked.has(id)), [resolved.order, locked]);
  const skipped = useMemo(() => new Set([...resolved.hidden, ...unavailable]), [resolved.hidden, unavailable]);
  const visibleColumns = useMemo(
    () =>
      resolved.order
        .filter((id) => !resolved.hidden.has(id) && !unavailable.has(id))
        .flatMap((id) => catalogue.filter((column) => column.id === id)),
    [catalogue, resolved, unavailable],
  );

  // Always writes the RESOLVED values, so the stored blob converges on the live
  // catalogue instead of carrying junk ids forward.
  //
  // Chained through `columnsWriteChain`: a drag can fire several of these before
  // the first PUT lands, and an unchained write can reach the server out of order,
  // leaving an older blob as the member's persisted layout.
  const columnsWriteChain = useRef<Promise<void>>(Promise.resolve());
  const applyColumns = useCallback(
    (next: ColumnPreference) => {
      // Written full, unavailable ids included, so a column the table cannot show right now
      // keeps its slot. The one exception is a column that is unavailable AND new: the member
      // never placed it, and writing it would silently spend its „neu" badge before they see it.
      const unplaced = (id: string) => unavailable.has(id) && resolved.added.has(id);
      const written = { order: next.order.filter((id) => !unplaced(id)), hidden: next.hidden };

      onPreferenceChange(written);

      columnsWriteChain.current = columnsWriteChain.current.then(async () => {
        try {
          if (!(await savePreference(written))) toast.danger(t('save-error'));
        } catch {
          toast.danger(t('save-error'));
        }
      });
    },
    [savePreference, onPreferenceChange, toast, t, unavailable, resolved.added],
  );

  const applyOrder = useCallback(
    (order: ReadonlyArray<string>) => applyColumns({ order: [...order], hidden: [...resolved.hidden] }),
    [applyColumns, resolved],
  );

  const panelRows: ReadonlyArray<ColumnPanelRow> = useMemo(
    () =>
      resolved.order.flatMap((id) => {
        if (unavailable.has(id)) return [];

        const column = catalogue.find((entry) => entry.id === id);

        return column === undefined
          ? []
          : [
              {
                id,
                label: column.label,
                visible: !resolved.hidden.has(id),
                alwaysVisible: id === alwaysVisibleId,
                locked: locked.has(id),
                isNew: resolved.added.has(id),
              },
            ];
      }),
    [resolved, catalogue, alwaysVisibleId, unavailable, locked],
  );

  // Resolved, never raw: the stored blob may carry ids the catalogue dropped.
  const setColumnVisibility = useCallback(
    (id: string, visible: boolean) =>
      applyColumns({ order: [...resolved.order], hidden: [...setColumnVisible(resolved.hidden, id, visible)] }),
    [resolved, applyColumns],
  );

  // Writes the EMPTY preference, not the explicit catalogue order, so a reset
  // member receives a future column silently instead of a „neu" badge.
  const resetColumns = useCallback(() => applyColumns(EMPTY_COLUMN_PREFERENCE), [applyColumns]);

  const reorderFromPanel = useCallback(
    (subset: ReadonlyArray<string>) => {
      const next = mergeSubsetOrder(resolved.order, subset);

      if (next !== resolved.order) applyOrder(next);
    },
    [resolved.order, applyOrder],
  );

  const drag = useColumnDrag(resolved.order, applyOrder);
  const lastVisibleId = visibleColumns.at(-1)?.id ?? null;

  // The visible column after `id`, or null at the end — so a drop on the right half
  // of the last column inserts at the end, which is what `reorderColumn(…, null)` means.
  const nextVisibleId = useCallback(
    (id: string): string | null => {
      const index = visibleColumns.findIndex((column) => column.id === id);

      return visibleColumns[index + 1]?.id ?? null;
    },
    [visibleColumns],
  );

  // A class on cells that already render, not a measured overlay: nothing to
  // re-measure on scroll, and it runs the table's full height. The side-specific
  // colour keeps tailwind-merge from repainting the row separators.
  const markerClass = useCallback(
    (id: string): string | false => {
      if (drag.target === null || drag.draggedId === null) return false;

      if (drag.target.beforeId === id) return 'border-l-2 border-l-interactive';

      return drag.target.beforeId === null && id === lastVisibleId ? 'border-r-2 border-r-interactive' : false;
    },
    [drag.target, drag.draggedId, lastVisibleId],
  );

  const trigger = useMemo<ColumnsTriggerProps>(
    () => ({
      open: panelOpen,
      onOpenChange: setPanelOpen,
      rows: panelRows,
      onSetVisible: setColumnVisibility,
      onReset: resetColumns,
      onReorder: reorderFromPanel,
      ...(describedBy === undefined ? {} : { describedBy }),
    }),
    [panelOpen, panelRows, setColumnVisibility, resetColumns, reorderFromPanel, describedBy],
  );

  const headerRowProps = useMemo(
    () => ({
      // On the ROW, not per cell: `dragleave` on the cell being left fires
      // before `dragover` on the one entered, so a per-cell clear flickers.
      onDragLeave: (event: Parameters<DragEventHandler<HTMLTableRowElement>>[0]) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) drag.clear();
      },
    }),
    [drag.clear],
  );

  const headCellProps = useCallback(
    (id: string) => {
      const className = cn('group/col', markerClass(id), drag.draggedId !== null && 'select-none');

      if (locked.has(id)) return { className };

      return {
        onDragOver: (event: Parameters<DragEventHandler<HTMLTableCellElement>>[0]) => {
          if (drag.draggedId === null) return;

          // Without preventDefault the browser refuses the drop and `onDrop` never fires.
          event.preventDefault();

          const rect = event.currentTarget.getBoundingClientRect();
          const before = event.clientX < rect.left + rect.width / 2;

          drag.hover({ beforeId: before ? id : nextVisibleId(id) });
        },
        onDrop: (event: Parameters<DragEventHandler<HTMLTableCellElement>>[0]) => {
          event.preventDefault();
          drag.drop();
        },
        className,
      };
    },
    [drag, markerClass, nextVisibleId, locked],
  );

  const headControls = useCallback(
    (id: string): ColumnHeadControls => {
      const isLocked = locked.has(id);
      // Locked ids are always visible, so they are never part of the movable count.
      const movableVisibleCount = visibleColumns.length - lead.length;

      return {
        columnLabel: catalogue.find((column) => column.id === id)?.label ?? id,
        draggable: !isLocked && movableVisibleCount > 1,
        dragged: drag.draggedId === id,
        onDragStart: () => drag.start(id),
        onDragEnd: drag.clear,
        canMoveLeft: !isLocked && canMove(tail, skipped, id, 'left'),
        canMoveRight: !isLocked && canMove(tail, skipped, id, 'right'),
        onMove: (direction) => applyOrder([...lead, ...moveColumn(tail, skipped, id, direction)]),
        onHide: id === alwaysVisibleId || isLocked ? null : () => setColumnVisibility(id, false),
        onOpenPanel: () => setPanelOpen(true),
      };
    },
    [drag, lead, tail, locked, skipped, visibleColumns, catalogue, alwaysVisibleId, applyOrder, setColumnVisibility],
  );

  const bodyCellClassName = useCallback(
    (id: string) =>
      cn(markerClass(id), drag.draggedId === id && 'opacity-45', drag.draggedId !== null && 'select-none'),
    [markerClass, drag.draggedId],
  );

  return useMemo(
    () => ({ visibleColumns, trigger, headerRowProps, headCellProps, headControls, bodyCellClassName }),
    [visibleColumns, trigger, headerRowProps, headCellProps, headControls, bodyCellClassName],
  );
}
